/**
 * Ops view of what ChooseChow holds for chefs and what chefs owe it.
 *
 * Two figures, and the panel is careful not to collapse them into one number.
 * Held money is owed TO a chef whose payout destination could not be used; owed
 * money is what chefs owe ChooseChow after a refund they caused. They can exist
 * for the same chef at once, so a single total would hide which side of the
 * balance the money is on — which is the whole reason an operator opens this page.
 *
 * Every figure is read from the server. Nothing here computes a balance.
 */

import { useEffect, useRef, useState } from 'react'
import { MinimalAlert, MinimalButton } from '@ovasabi/ui-minimal'
import { z } from 'zod'
import { commandPayload, commands } from '../../lib/runtime'
import * as S from './OpsStyle'
import { formatNaira } from './opsData'
import { OpsPayoutDialog, type PayoutTarget } from './OpsPayoutDialog'

const minor = z.coerce.number().int().safe()

const chefSchema = z.object({
  vendor_profile_id: z.string().min(1),
  kitchen_name: z.string(),
  destination_status: z.string(),
  held_minor: minor,
  held_order_count: z.coerce.number().int().nonnegative(),
  owed_minor: minor,
  choosechow_owes_minor: minor,
  net_minor: z.coerce.number().int(),
  last_activity_at: z.string(),
  releasing_minor: minor.default(0),
  paid_out_minor: minor.default(0),
  payable: z.coerce.boolean().default(false),
  last_release_status: z.string().default(''),
  last_release_reason: z.string().default(''),
})

const receivablesSchema = z.object({
  chefs: z.array(chefSchema),
  total_held_minor: z.coerce.number().int(),
  total_owed_minor: z.coerce.number().int(),
  net_minor: z.coerce.number().int(),
  chef_count: z.coerce.number().int().nonnegative(),
  truncated: z.coerce.boolean(),
})

type Chef = z.infer<typeof chefSchema>
type Receivables = z.infer<typeof receivablesSchema>

const PAGE_SIZE = 25

/**
 * Why money is being held, in the operator's words.
 *
 * The status is the payout destination's own state, which is what the capture
 * path branches on, so explaining it here matches why the money was not routed.
 */
const heldReason = (status: string): string => {
  switch (status) {
    case 'restricted':
      return 'Paystack will not settle to this destination'
    case 'failed':
      return 'Subaccount could not be created'
    case 'pending':
      return 'Payout identity not yet approved'
    case '':
      return 'No payout identity registered'
    default:
      return `Destination is ${status}`
  }
}

export const OpsReceivablesPanel = () => {
  const [data, setData] = useState<Receivables | null>(null)
  const [offset, setOffset] = useState(0)
  const [error, setError] = useState('')
  const [attempt, setAttempt] = useState(0)
  const [loading, setLoading] = useState(true)
  const pending = useRef(false)
  const [paying, setPaying] = useState<PayoutTarget | null>(null)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    void commands.billing
      .listPayoutReceivables(offset, PAGE_SIZE)
      .then((response) => {
        if (active) setData(receivablesSchema.parse(commandPayload(response)))
      })
      .catch((err: unknown) => {
        // The server's reason is shown so a refusal (such as a missing grant)
        // is not mistaken for a transient failure that Retry would clear. A
        // parse failure's message is schema detail, not something to act on.
        const reason = err instanceof Error && !(err instanceof z.ZodError) ? err.message.trim() : ''
        if (active) {
          setError(`Could not read the receivables position${reason ? ` (${reason})` : ''}. Retry before acting on it.`)
        }
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [offset, attempt])

  const chefRow = (chef: Chef) => (
    <tr key={chef.vendor_profile_id}>
      <td>{chef.kitchen_name || 'Unnamed kitchen'}</td>
      <td>
        {formatNaira(chef.held_minor)}
        {chef.held_order_count > 1 && (
          <S.Note> across {chef.held_order_count} orders</S.Note>
        )}
        {chef.held_minor > 0 && !chef.payable && <S.Note>{heldReason(chef.destination_status)}</S.Note>}
        {chef.releasing_minor > 0 && <S.Note>{formatNaira(chef.releasing_minor)} on its way via Paystack</S.Note>}
        {chef.last_release_status === 'failed' && (
          <S.ErrorNote>Last payout failed{chef.last_release_reason ? `: ${chef.last_release_reason}` : ''}</S.ErrorNote>
        )}
        {chef.held_minor > 0 && chef.releasing_minor === 0 && (
          <MinimalButton
            size="sm"
            variant={chef.payable ? 'primary' : 'secondary'}
            onClick={() => setPaying({
              vendorProfileId: chef.vendor_profile_id, kitchenName: chef.kitchen_name,
              heldMinor: chef.held_minor, payable: chef.payable,
            })}
          >
            {chef.payable ? 'Pay now' : 'Record payment'}
          </MinimalButton>
        )}
      </td>
      <td>{formatNaira(chef.owed_minor)}</td>
      <td>{formatNaira(chef.net_minor)}</td>
    </tr>
  )

  return (
    <S.Card>
      <S.CardTitle>Chef receivables</S.CardTitle>
      {loading && <p role="status">Reading the position…</p>}
      {error && (
        <MinimalAlert tone="danger" title="Could not load receivables">
          {error}
          <MinimalButton
            disabled={pending.current}
            onClick={() => setAttempt((n) => n + 1)}
          >
            Retry
          </MinimalButton>
        </MinimalAlert>
      )}
      {data && !loading && (
        <>
          <S.StatRow>
            <S.Stat>
              <S.StatValue>{formatNaira(data.total_held_minor)}</S.StatValue>
              <S.StatLabel>Held for chefs (owed to them)</S.StatLabel>
            </S.Stat>
            <S.Stat>
              <S.StatValue>{formatNaira(data.total_owed_minor)}</S.StatValue>
              <S.StatLabel>Owed by chefs (recoverable)</S.StatLabel>
            </S.Stat>
            <S.Stat>
              <S.StatValue>{formatNaira(data.net_minor)}</S.StatValue>
              <S.StatLabel>Net position</S.StatLabel>
            </S.Stat>
            <S.Stat>
              <S.StatValue>{data.chef_count}</S.StatValue>
              <S.StatLabel>Kitchens with an open position</S.StatLabel>
            </S.Stat>
          </S.StatRow>

          <S.Note>
            Held money is paid out by Paystack transfer once the kitchen is verified and
            its payout account is active — automatically each day when automatic payouts
            are switched on, or now with Pay now. Record payment marks money you paid
            outside Paystack. Amounts owed by chefs are recovered from their next orders,
            never withheld from a payout.
          </S.Note>

          {data.chefs.length === 0 ? (
            <S.Note>
              No kitchen currently has money held or owes ChooseChow.
            </S.Note>
          ) : (
            <S.TableScroll>
              <S.Table>
                <thead>
                  <tr>
                    <th>Kitchen</th>
                    <th>Held for them</th>
                    <th>They owe</th>
                    <th>Net</th>
                  </tr>
                </thead>
                <tbody>{data.chefs.map(chefRow)}</tbody>
              </S.Table>
            </S.TableScroll>
          )}

          {/*
            The totals above are the PAGE's totals, not the whole position, so
            they are labelled as such whenever more remains. Reading a partial
            total as the business-wide figure is the mistake this guards.
          */}
          {data.truncated && (
            <S.Note>
              More kitchens have an open position than are shown here, so these
              totals cover this page only.
            </S.Note>
          )}
          {(offset > 0 || data.truncated) && (
            <S.Note>
              <MinimalButton
                disabled={loading || offset === 0}
                onClick={() => setOffset((n) => Math.max(0, n - PAGE_SIZE))}
              >
                Previous
              </MinimalButton>{' '}
              <S.Note>
                {data.chef_count === 0 ? 'No rows' : `Rows ${offset + 1}–${offset + data.chef_count}`}
              </S.Note>{' '}
              <MinimalButton
                disabled={loading || !data.truncated}
                onClick={() => setOffset((n) => n + PAGE_SIZE)}
              >
                Next
              </MinimalButton>
            </S.Note>
          )}
        </>
      )}
      {paying && (
        <OpsPayoutDialog
          target={paying}
          onClose={() => setPaying(null)}
          onDone={() => setAttempt((n) => n + 1)}
        />
      )}
    </S.Card>
  )
}
